// src/modules/shell-cliente/rdoCliente.js — Feature 013 (T-09)
// Portal do cliente: ler RDO disponibilizado, ASSINAR (signaturePad → rdo_assinar_cliente → gera PDF)
// ou RECUSAR com motivo (rdo_recusar_cliente). RPCs SECURITY DEFINER (mig 53).
import { sb } from '../../api/supabaseClient.js';
import { openModal } from '../../components/modal.js';
import { showToast } from '../../lib/toast.js';
import { fmtDate } from '../../lib/format.js';
import { esc } from '../../lib/dom.js';
import { signFotosUrls, abrirArquivoSeguro } from '../../lib/signedUrl.js';
import { signaturePadHTML, mountSignaturePad } from '../../components/signaturePad.js';

const ST = {
  pendente_assinatura: ['var(--sc-warn)', 'Assinar'],
  assinado: ['var(--sc-ok)', 'Assinado'],
  recusado: ['var(--sc-bad)', 'Recusado'],
};
function badge(st) { const [c, l] = ST[st] || ['var(--sc-idle)', st || '—']; return `<span style="font-size:11px;font-weight:700;color:${c};background:${c}1a;border:1px solid ${c}55;border-radius:999px;padding:3px 10px">${l}</span>`; }

export async function loadRdosCliente(obras) {
  const out = [];
  await Promise.all((obras || []).map(async (o) => {
    try {
      const { data } = await sb.rpc('rdo_para_cliente', { p_obra_id: o.id });
      for (const r of (data || [])) { r._obraNome = o.nome; out.push(r); }
    } catch (_) {}
  }));
  out.sort((a, b) => String(b.data_registro).localeCompare(String(a.data_registro)));
  return out;
}

export function pendCount(rdos) { return (rdos || []).filter((r) => r.status_doc === 'pendente_assinatura').length; }

export function rdoClienteSectionHTML(rdos) {
  if (!rdos || !rdos.length) return '';
  const pend = pendCount(rdos);
  return `<div class="cli-rdo-sec" style="margin:4px 0 18px">
    <div style="display:flex;align-items:center;gap:8px;margin:0 2px 10px">
      <div style="font-size:13px;font-weight:700;color:var(--text)">Relatórios diários (RDO)</div>
      ${pend ? `<span style="font-size:11px;font-weight:700;color:var(--sc-warn-fg);background:var(--sc-warn);border-radius:999px;padding:2px 9px">${pend} para assinar</span>` : ''}
    </div>
    ${rdos.map((r) => `
      <div class="cli-mob-file" role="button" tabindex="0" data-rdo-id="${r.id}" style="text-align:left;cursor:pointer;${r.status_doc === 'pendente_assinatura' ? 'border-color:var(--sc-warn)' : ''}">
        <div class="cli-mob-file-ico" style="color:${(ST[r.status_doc] || [])[0] || 'var(--sc-info)'}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15l2 2 4-4"/></svg>
        </div>
        <div class="cli-mob-file-txt">
          <div class="cli-mob-file-nome">RDO ${r.numero_rdo ? 'Nº ' + String(r.numero_rdo).padStart(4, '0') : ''} — ${fmtDate(r.data_registro)}</div>
          <div class="cli-mob-file-meta">${esc(r._obraNome || '')}</div>
        </div>
        <span style="margin-left:auto;align-self:center;flex:none;white-space:nowrap">${badge(r.status_doc)}</span>
      </div>`).join('')}
  </div>`;
}

export function wireRdoList(root, onDone) {
  root?.querySelectorAll('[data-rdo-id]')?.forEach((el) => {
    el.addEventListener('click', () => openRdoCliente(el.dataset.rdoId, onDone));
  });
}

function _row(label, val) { if (!val && val !== 0) return ''; return `<div style="display:flex;gap:10px;padding:6px 0;border-bottom:1px solid var(--border,rgba(148,163,184,.16))"><div style="font-size:11px;color:var(--text3);min-width:104px;text-transform:uppercase;letter-spacing:.04em">${label}</div><div style="font-size:13px;color:var(--text);flex:1">${esc(String(val))}</div></div>`; }

export async function openRdoCliente(registroId, onDone) {
  const { data, error } = await sb.rpc('rdo_detalhe_cliente', { p_registro_id: registroId });
  if (error || !data?.ok) { showToast(error?.message || 'Não foi possível abrir o RDO.', 'error'); return; }
  const d = data.data;
  if (Array.isArray(d.fotos)) await signFotosUrls(d.fotos);
  const isPend = d.status_doc === 'pendente_assinatura';
  const efetivo = Array.isArray(d.efetivo_por_funcao) ? d.efetivo_por_funcao.map((f) => `${f.funcao || f.nome || '—'} (${f.qtd ?? ''})`).join(', ') : '';
  const epis = Array.isArray(d.epis_em_uso) ? d.epis_em_uso.join(', ') : '';
  const conteudo = `
    <div style="margin-bottom:10px">${badge(d.status_doc)} ${d.contratada?.nome ? `<span style="font-size:11.5px;color:var(--text3);margin-left:6px">Assinado pela contratada: ${esc(d.contratada.nome)}</span>` : ''}</div>
    ${_row('Clima', d.clima)}
    ${_row('Efetivo', d.efetivo_total != null ? d.efetivo_total + ' trabalhadores' : '')}
    ${_row('Por função', efetivo)}
    ${_row('Atividades', d.atividades_realizadas)}
    ${_row('Equipamentos', d.equipamentos)}
    ${_row('Mat. recebidos', d.materiais_recebidos)}
    ${_row('Mat. aplicados', d.materiais_aplicados)}
    ${_row('DDS', d.dds_realizado ? 'Realizado' : 'Não registrado')}
    ${_row('EPIs', epis)}
    ${_row('Ocorrências', d.ocorrencias_seguranca || d.ocorrencias)}
    ${_row('Observações', d.observacoes)}
    ${(d.fotos && d.fotos.length) ? `<div style="margin-top:12px"><div style="font-size:11px;color:var(--text3);text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px">Fotos (${d.fotos.length})</div><div style="display:grid;grid-template-columns:repeat(3,1fr);gap:6px">${d.fotos.map((f) => `<img src="${f.url}" style="width:100%;aspect-ratio:1;object-fit:cover;border-radius:8px" onclick="window.open('${f.url}','_blank')">`).join('')}</div></div>` : ''}`;

  let pad = null;
  const id = 'sig-cliente';
  const assinarBlock = isPend ? `
    <div style="margin-top:18px;border-top:2px solid var(--border,rgba(148,163,184,.2));padding-top:14px">
      <label style="display:block;font-size:12px;font-weight:700;margin-bottom:6px">Sua assinatura</label>
      ${signaturePadHTML({ id, hint: 'Assine com o dedo para aprovar este RDO.' })}
      <label style="display:block;font-size:12px;font-weight:600;margin:12px 0 5px">CPF <span style="color:var(--text3);font-weight:400">(opcional)</span></label>
      <input type="text" id="cli-sig-cpf" inputmode="numeric" placeholder="000.000.000-00" maxlength="14" style="width:100%">
      <details style="margin-top:12px"><summary style="font-size:12.5px;color:var(--sc-bad);cursor:pointer;font-weight:600">Tenho uma ressalva — recusar</summary>
        <textarea id="cli-recusa-motivo" rows="3" style="width:100%;margin-top:8px" placeholder="Descreva o motivo da recusa (mín. 5 caracteres)."></textarea>
      </details>
      <div style="font-size:11px;color:var(--text3);margin-top:10px;line-height:1.5">Ao assinar, você concorda com o conteúdo deste RDO. A assinatura eletrônica (Lei 14.063/2020) registra seu nome, data/hora, IP e o hash do documento.</div>
    </div>` : (d.status_doc === 'recusado' && d.motivo_recusa ? `<div style="margin-top:14px;background:var(--sc-bad)1a;border:1px solid var(--sc-bad)55;border-radius:10px;padding:10px 12px;font-size:12.5px;color:var(--sc-bad)">Recusado: ${esc(d.motivo_recusa)}</div>` : '');

  const buttons = isPend ? [
    { label: 'Recusar', kind: 'danger', action: async () => {
        const motivo = (document.getElementById('cli-recusa-motivo')?.value || '').trim();
        if (motivo.length < 5) { showToast('Para recusar, descreva o motivo (abra "Tenho uma ressalva").', 'error'); return false; }
        const { data: rr, error: er } = await sb.rpc('rdo_recusar_cliente', { p_registro_id: registroId, p_motivo: motivo });
        if (er || !rr?.ok) { showToast(rr?.message || er?.message || 'Erro ao recusar.', 'error'); return false; }
        showToast('RDO recusado. A construtora foi notificada da ressalva.', 'success');
        onDone?.(); return true;
      } },
    { label: 'Assinar', kind: 'primary', action: async () => {
        if (!pad || pad.isEmpty()) { showToast('Assine no quadro para aprovar.', 'error'); return false; }
        const cpf = (document.getElementById('cli-sig-cpf')?.value || '').trim() || null;
        const { data: rr, error: er } = await sb.rpc('rdo_assinar_cliente', { p_registro_id: registroId, p_assinatura_img: pad.toDataURL('image/png'), p_cpf: cpf });
        if (er || !rr?.ok) { showToast(rr?.message || er?.message || 'Erro ao assinar.', 'error'); return false; }
        try { pad.destroy(); } catch (_) {}
        showToast('Assinado! Gerando o documento…', 'success');
        try { await sb.functions.invoke('gerar-rdo-pdf', { body: { registro_id: registroId } }); } catch (_) {}
        showToast('RDO assinado. O documento já está em Arquivos.', 'success');
        onDone?.(); return true;
      } },
  ] : [
    ...(d.pdf_assinado_url ? [{ label: 'Baixar PDF', kind: 'primary', action: async () => { await abrirArquivoSeguro(d.pdf_assinado_url); return false; } }] : []),
    { label: 'Fechar', action: 'cancel' },
  ];

  openModal({ title: `RDO ${d.numero_rdo ? 'Nº ' + String(d.numero_rdo).padStart(4, '0') : ''}`, codigo: 'RDO', sub: fmtDate(d.data_registro), bodyHTML: conteudo + assinarBlock, size: 'large', buttons });
  if (isPend) pad = mountSignaturePad(id);
}
